import { Send as SendIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import UserHint from "../../../components/display/UserHint";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { SEND_INBOX } from "../../../enums/PermissionTitles";
import usePut, { usePutNoFeedback } from "../../../hooks/usePut";
import useUser from "../../../hooks/useUser";
import { useIsMobile } from "../../../utils/Functions";
import dayjs from "dayjs";
import { CONVO_TO_UPDATE_ID } from "../../../enums/QueryKeys";
import { useQueryClient } from "@tanstack/react-query";

export default function SendMessage({ scrollToBottom, isMasterSender, isReceiverOrSenderDeleted }) {
  const [newMessage, setNewMessage] = React.useState("");
  const queryClient = useQueryClient();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isMobile = useIsMobile();
  const hasPermission = isPermissionAllowed(SEND_INBOX);
  const [searchParams] = useSearchParams();
  const readFilter = searchParams.get("read_f") ?? false;
  const convoId = searchParams.get("convo");
  const { t } = useTranslation();
  const put = usePutNoFeedback(
    "/inbox/update/conversation/",
    "messages_for_convo" + convoId
  );

  const { roles } = useUser();
  if (roles.user) return <Hint hint={"CantSendMessagesAsUser"} />;
  if (!roles.master && isMasterSender) {
    return <Hint hint={"CantSendMessagesToMaster"} />;
  } else if (!hasPermission) {
    return <Hint hint={"NoPermissionToSendMessages"} />;
  }
  if (isReceiverOrSenderDeleted) {
    return <Hint hint={"CantSendMessagesToDeletedUser"} />;
  }

  async function handleSendMessage() {
    if (!newMessage.trim()) return;

    const dto = {
      id: convoId,
      message: newMessage,
    };

    const tempMessage = newMessage;
    setNewMessage("");

    const res = await put.submitCallback(dto);
    if (res) {
      queryClient.setQueryData(
        ["conversations"],
        (old) => {
          if (!old) return old;
          const lastMessage = res?.at(-1);
          let body = lastMessage?.body;
          if (String(body).length > 20) {
            body = `${String(body).slice(0, 20)}...`;
          }
          return old.map((conv) => {
            if (Number(conv.id) === Number(convoId)) {
              return {
                ...conv,
                preview: body,
                sentAt: lastMessage?.sentAt,
              };
            }
            return conv;
          });
        }
      );

      queryClient.setQueryData([`messages_for_convo${convoId}`], (old) => {
        if (!old) return res;

        const existingIds = new Set(old.map((msg) => msg.id));
        const newMessages = res.filter((msg) => !existingIds.has(msg.id));

        return [...old, ...newMessages];
      });

      queryClient.setQueryData(CONVO_TO_UPDATE_ID, convoId);
      scrollToBottom();
    } else {
      setNewMessage(tempMessage);
    }
  }

  if (isMobile) {
    return (
      <Paper elevation={2} sx={{ p: 1 }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                variant="outlined"
                placeholder={`${t("TypeYourMessage")}...`}
                value={newMessage}
                maxRows={6}
                onChange={(e) => setNewMessage(e.target.value)}
                size="small"
                sx={{ flexGrow: 1 }}
                inputProps={{
                  maxLength: 5000,
                  "data-testid": "inbox.chat.message-input",
                }}
              />
              <IconButton
                type="submit"
                color="primary"
                title={t("Send")}
                disabled={!newMessage.trim()}
                data-testid="inbox.chat.send-button"
                sx={{
                  alignSelf: "flex-end",
                  height: "40px",
                  whiteSpace: "nowrap",
                }}
              >
                <SendIcon />
              </IconButton>
            </Stack>
            <UserHint hint={"UsersCantReplyToYourMessages"} />
          </Stack>
        </form>
      </Paper>
    );
  }

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 2 }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
            <TextField
              fullWidth
              multiline
              variant="outlined"
              placeholder={`${t("TypeYourMessage")}...`}
              value={newMessage}
              maxRows={6}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              size="small"
              sx={{ flexGrow: 1 }}
              inputProps={{ maxLength: 5000 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              disabled={!newMessage.trim()}
              sx={{
                alignSelf: "flex-end",
                height: "40px",
                whiteSpace: "nowrap",
              }}
            >
              {t("Send")}
            </Button>
          </Stack>
          <UserHint hint={"UsersCantReplyToYourMessages"} />
        </form>
      </Box>
    </Paper>
  );
}

function Hint({ hint }) {
  return (
    <Stack p={2} spacing={2}>
      <UserHint hint={hint} />
    </Stack>
  );
}
